import { Fragment, useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Box, Button, Chip, Dialog, DialogTitle, IconButton, List, ListItem, ListItemButton,
  ListItemText, Stack, TextField, Typography
} from '@mui/material'
import { Add, Delete, Warning } from '@mui/icons-material'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { Group, GroupMembership, ID, SiteAccessAccount, User } from '../types/models'
import { PageSubtitle } from './atoms/PageSubtitle'

interface Props {
  user: User
}

/**
 * Admin controls for moving a user between groups.
 *
 * Two distinct mechanisms are surfaced (see UserGroupAccessTest on the server):
 *   - **Group memberships** — being a member of a group makes the user administerable (and
 *     impersonatable) by every admin of that group. Adding/removing here is how a sales/install
 *     company takes over (or relinquishes) a customer.
 *   - **Direct site access** — accounts holding a role on the user's *personal* group, which lets
 *     them see the user's site/dashboard directly without impersonation. Root only.
 */
export function UserGroupsManager({ user }: Props): React.ReactElement {
  const app = useApp()
  const { t } = useTranslation()

  const [memberships, setMemberships] = useState<GroupMembership[]>([])
  const [siteAccess, setSiteAccess] = useState<SiteAccessAccount[]>([])

  const [removeTarget, setRemoveTarget] = useState<GroupMembership | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [allGroups, setAllGroups] = useState<Group[]>([])
  const [groupFilter, setGroupFilter] = useState('')

  const [revokeTarget, setRevokeTarget] = useState<SiteAccessAccount | null>(null)
  const [grantOpen, setGrantOpen] = useState(false)
  const [grantEmail, setGrantEmail] = useState('')

  const isRoot = app.user?.role === 'ROOT'

  const loadGroups = useCallback(() => {
    callAxios<GroupMembership[]>({ url: `/users/${user.id}/groups` })
      .then(res => res.success ? setMemberships(res.data) : app.addError(res))
  }, [user.id, app])

  const loadSiteAccess = useCallback(() => {
    callAxios<SiteAccessAccount[]>({ url: `/users/${user.id}/site-access` })
      .then(res => res.success ? setSiteAccess(res.data) : app.addError(res))
  }, [user.id, app])

  useEffect(() => {
    loadGroups()
    if (isRoot) {
      loadSiteAccess()
    }
  }, [loadGroups, loadSiteAccess, isRoot])

  // Groups the user is still in (other than permanent ones) — used for the takeover warning.
  const nonPermanent = memberships.filter(m => !m.permanent)
  const memberIds = new Set(memberships.map(m => m.id))

  const selectableGroups = allGroups
    .filter(g => !g.permanent && !memberIds.has(g.id as ID))
    .filter(g => g.name.toLowerCase().includes(groupFilter.toLowerCase()))

  const openAdd = () => {
    callAxios<Group[]>({ url: '/user-groups' })
      .then(res => res.success ? setAllGroups(res.data) : app.addError(res))
    setGroupFilter('')
    setAddOpen(true)
  }

  const onAddToGroup = (group: Group) => {
    callAxios({ method: 'POST', url: `/user-groups/${group.id}/users` }, { user_id: user.id, role: 'READER' })
      .then(res => {
        if (res.success) {
          app.addInfo(t('User added to group.'))
          loadGroups()
          setAddOpen(false)
        } else {
          app.addError(res)
        }
      })
  }

  const onRemoveFromGroup = () => {
    if (!removeTarget) {
      return
    }
    callAxios({ method: 'DELETE', url: `/user-groups/${removeTarget.id}/users/${user.id}` })
      .then(res => {
        if (res.success) {
          loadGroups()
        } else {
          app.addError(res)
        }
        setRemoveTarget(null)
      })
  }

  const onGrant = async () => {
    const email = grantEmail.trim()
    if (!email) {
      return
    }
    const lookup = await callAxios<{ id: ID, email: string }[]>({ url: `/users?email=${encodeURIComponent(email)}` })
    if (!lookup.success) {
      app.addError(lookup)
      return
    }
    const match = lookup.data.find(u => u.email.toLowerCase() === email.toLowerCase())
    if (!match) {
      app.addError(t('No user found with that email.'))
      return
    }
    const res = await callAxios({ method: 'POST', url: `/users/${user.id}/site-access` }, { user_id: match.id })
    if (res.success) {
      app.addInfo(t('Site access granted.'))
      loadSiteAccess()
      setGrantOpen(false)
      setGrantEmail('')
    } else {
      app.addError(res)
    }
  }

  const onRevoke = () => {
    if (!revokeTarget) {
      return
    }
    callAxios({ method: 'DELETE', url: `/users/${user.id}/site-access/${revokeTarget.id}` })
      .then(res => {
        if (res.success) {
          loadSiteAccess()
        } else {
          app.addError(res)
        }
        setRevokeTarget(null)
      })
  }

  return (
    <Fragment>
      <Box data-cy="box-Group Memberships">
        <PageSubtitle title={t('Group Memberships')}/>
        <List>
          {memberships.map(m => (
            <ListItem
              key={m.id}
              data-cy={`group-membership-${m.id}`}
              secondaryAction={
                m.permanent
                  ? <Chip size="small" variant="outlined" label={t('permanent')}/>
                  : <IconButton
                      data-cy={`button-remove-group-${m.id}`}
                      sx={{ color: 'error.dark' }}
                      onClick={() => setRemoveTarget(m)}
                    ><Delete/></IconButton>
              }
            >
              <ListItemText primary={m.name}/>
              <Chip size="small" sx={{ mr: 2 }} label={m.role}/>
            </ListItem>
          ))}
          {memberships.length === 0 && <ListItem><ListItemText primary={t('No groups.')}/></ListItem>}
        </List>
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" color="secondary" data-cy="button-add-to-group" endIcon={<Add/>} onClick={openAdd}>
            <Trans>Add to group</Trans>
          </Button>
        </Box>
      </Box>

      {isRoot &&
        <Box sx={{ mt: 4 }} data-cy="box-Direct Site Access">
          <PageSubtitle title={t('Direct Site Access')}/>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            <Trans>These accounts see this user's site directly, without logging in as the user.</Trans>
          </Typography>
          <List>
            {siteAccess.map(a => (
              <ListItem
                key={a.id}
                data-cy={`site-access-${a.id}`}
                secondaryAction={
                  <IconButton
                    data-cy={`button-revoke-access-${a.id}`}
                    sx={{ color: 'error.dark' }}
                    onClick={() => setRevokeTarget(a)}
                  ><Delete/></IconButton>
                }
              >
                <ListItemText primary={a.email}/>
                <Chip size="small" sx={{ mr: 2 }} label={a.role}/>
              </ListItem>
            ))}
            {siteAccess.length === 0 && <ListItem><ListItemText primary={t('No accounts have direct access.')}/></ListItem>}
          </List>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            <Button variant="outlined" color="secondary" data-cy="button-grant-access" endIcon={<Add/>} onClick={() => setGrantOpen(true)}>
              <Trans>Grant site access</Trans>
            </Button>
          </Box>
        </Box>
      }

      {removeTarget &&
        <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
          <DialogTitle sx={{ backgroundColor: 'error.light' }}><Trans>Remove from group?</Trans></DialogTitle>
          <Box sx={{ p: 2 }}>
            <Typography>
              {t('Remove {{email}} from {{group}}?', { email: user.email, group: removeTarget.name })}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ m: 2 }}>
              <Button color="primary" onClick={() => setRemoveTarget(null)}><Trans>Cancel</Trans></Button>
              <Button color="secondary" data-cy="button-remove-group-OK" onClick={onRemoveFromGroup}><Trans>OK</Trans></Button>
            </Stack>
          </Box>
        </Dialog>
      }

      {addOpen &&
        <Dialog fullWidth open={addOpen} onClose={() => setAddOpen(false)}>
          <DialogTitle><Trans>Add to group</Trans></DialogTitle>
          <Box sx={{ px: 2 }}>
            {nonPermanent.length > 0 &&
              <Box data-cy="box-still-in-groups" sx={{ p: 2, mb: 1, backgroundColor: 'error.light', borderRadius: 1 }}>
                <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                  <Warning sx={{ mr: 1 }}/><Trans>This user is still in other groups</Trans>
                </Typography>
                <Box sx={{ mt: 1 }}>{nonPermanent.map(m => m.name).join(', ')}</Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Trans>If a new company is taking over, you usually want to remove these first.</Trans>
                </Typography>
              </Box>
            }
            <TextField
              fullWidth
              autoFocus
              margin="normal"
              data-cy="text-group-filter"
              label={t('Search groups')}
              value={groupFilter}
              onChange={e => setGroupFilter(e.target.value)}
            />
            <List sx={{ maxHeight: 320, overflow: 'auto' }}>
              {selectableGroups.map(g => (
                <ListItemButton key={g.id} data-cy={`add-group-option-${g.id}`} onClick={() => onAddToGroup(g)}>
                  <ListItemText primary={g.name}/>
                </ListItemButton>
              ))}
              {selectableGroups.length === 0 && <ListItem><ListItemText primary={t('No matching groups.')}/></ListItem>}
            </List>
          </Box>
          <Stack direction="row" spacing={2} sx={{ m: 2 }}>
            <Button color="primary" onClick={() => setAddOpen(false)}><Trans>Cancel</Trans></Button>
          </Stack>
        </Dialog>
      }

      {revokeTarget &&
        <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)}>
          <DialogTitle sx={{ backgroundColor: 'error.light' }}><Trans>Revoke site access?</Trans></DialogTitle>
          <Box sx={{ p: 2 }}>
            <Typography>
              {t('Revoke direct site access for {{email}}?', { email: revokeTarget.email })}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ m: 2 }}>
              <Button color="primary" onClick={() => setRevokeTarget(null)}><Trans>Cancel</Trans></Button>
              <Button color="secondary" data-cy="button-revoke-access-OK" onClick={onRevoke}><Trans>OK</Trans></Button>
            </Stack>
          </Box>
        </Dialog>
      }

      {grantOpen &&
        <Dialog fullWidth open={grantOpen} onClose={() => setGrantOpen(false)}>
          <DialogTitle><Trans>Grant site access</Trans></DialogTitle>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <Trans>Enter the email of the account that should see this user's site directly.</Trans>
            </Typography>
            <TextField
              fullWidth
              autoFocus
              margin="normal"
              type="email"
              data-cy="text-grant-email"
              label={t('Email')}
              value={grantEmail}
              onChange={e => setGrantEmail(e.target.value)}
              onKeyUp={e => { if (e.key === 'Enter') { onGrant() } }}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button color="primary" onClick={() => setGrantOpen(false)}><Trans>Cancel</Trans></Button>
              <Button color="secondary" data-cy="button-grant-access-OK" onClick={onGrant}><Trans>OK</Trans></Button>
            </Stack>
          </Box>
        </Dialog>
      }
    </Fragment>
  )
}
